/**
 * La complétude de l'espace de travail, lisible depuis plusieurs écrans.
 *
 * Extrait de `GettingStartedComponent`, qui était le seul à savoir assembler les
 * stores et à appeler `computeWorkspaceSetup`. Le Cockpit a besoin du MÊME
 * chiffre pour sa bande « Mise en route » : dupliquer l'assemblage, c'est
 * accepter que les deux surfaces divergent au premier item ajouté.
 *
 * Ce service ne décide de RIEN — il lit les stores, en fait un instantané plat
 * et le passe à l'util pur. Toute la logique de complétude, et donc tous les cas
 * limites, reste dans `getting-started.util.ts`, testée sans TestBed.
 *
 * ── Pourquoi il vit ici et pas dans `shared/services/` ────────────────────────
 * Il importe `getting-started.util.ts`, qui est sous `pages/apps/`. Le ratchet
 * `scripts/check-layer-boundaries.js` interdit à `shared/` d'importer une page :
 * le déplacer inverserait la dépendance. Un import `pages` → `pages` (le Cockpit
 * vers ici) n'est pas gardé.
 *
 * ── Pourquoi `activate()` ────────────────────────────────────────────────────
 * Trois services n'existent ici que pour brancher leur écoute Firestore
 * (`TenantsService`, `ContractsService`, `InventoryReportService`) : sans elle,
 * la complétude annoncerait « aucun locataire » sur un espace qui en compte.
 * S'y ajoutent une lecture des documents, une des prestataires et le chargement
 * des profils de facturation. Fait dans le constructeur d'un service `root`,
 * tout cela se paierait à CHAQUE ouverture du Cockpit, y compris pour quelqu'un
 * qui a masqué la bande. `activate()` est donc la porte : tant qu'aucun écran ne
 * l'a appelée, ce service ne lit rien.
 */

import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, map, of, switchMap, take } from 'rxjs';

import { AuthStore } from '@core/services/auth/auth.store';
import { ContractsService } from '@core/services/data/contracts.service';
import { DocumentService } from '@core/services/data/document.service';
import { InventoryReportService } from '@core/services/data/inventory-report.service';
import { ServiceProvidersService } from '@core/services/data/service-providers.service';
import { TenantsService } from '@core/services/data/tenants.service';
import { isResidential } from '@core/services/property-analysis/property-scoring.helpers';
import { PropertyScoringService } from '@core/services/property-analysis/property-scoring.service';

import { CertificateStatus, Property } from '@shared/definitions/property';
import { Tenant } from '@shared/definitions/tenant';
import { WorkspaceType } from '@shared/definitions/workspace';
import { ContractStore } from '@shared/services/contract.store';
import { InventoryReportStore } from '@shared/services/inventory-report.store';
import { PropertyStore } from '@shared/services/property.store';
import { TenantStore } from '@shared/services/tenant.store';
import { WorkspaceStore } from '@shared/services/workspace.store';

import { WORKSPACE_BILLING_PROFILES_STORE } from '../workspace-billing-profiles';
import {
  PropertySetupSnapshot,
  WorkspaceSetup,
  computeWorkspaceSetup
} from './getting-started.util';

/**
 * Types d'espace tenus de renseigner un numéro de TVA.
 *
 * Typé `ReadonlySet<string>` et non `ReadonlySet<WorkspaceType>` : le
 * `Workspace` exposé par `WorkspaceStore` vient de
 * `core/services/auth/workspace.service.types` — un modèle DISTINCT de
 * `shared/definitions/workspace`, où `type` n'est qu'une `string` optionnelle.
 * Cette duplication est de la dette gelée (CLAUDE.md) : on s'y adapte, on ne la
 * réunifie pas au passage.
 */
const PROFESSIONAL_WORKSPACE_TYPES: ReadonlySet<string> = new Set<WorkspaceType>([
  'professional',
  'business',
  'agency',
  'syndic',
  'service_provider'
]);

/** Compte par `propertyUID` — baux et états des lieux en portent un directement. */
function countByPropertyUID(
  entities: readonly { propertyUID?: string | null }[]
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const entity of entities) {
    const uid = entity.propertyUID;
    if (!uid) continue;
    counts.set(uid, (counts.get(uid) ?? 0) + 1);
  }
  return counts;
}

/**
 * Compte les locataires par bien. Un locataire porte une LISTE de biens
 * (`properties[]`), pas un `propertyUID` : même intention que
 * `TenantStore.byPropertyId`, mais en un seul passage sur la liste.
 */
function countTenantsByProperty(tenants: readonly Tenant[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const tenant of tenants) {
    const assignments = (tenant as Tenant & { properties?: { propertyUID?: string }[] }).properties;
    // Un même locataire ne compte qu'une fois par bien, même si la liste répète l'entrée.
    const seen = new Set<string>();
    for (const assignment of assignments ?? []) {
      const uid = assignment?.propertyUID;
      if (!uid || seen.has(uid)) continue;
      seen.add(uid);
      counts.set(uid, (counts.get(uid) ?? 0) + 1);
    }
  }
  return counts;
}

@Injectable({ providedIn: 'root' })
export class WorkspaceSetupService {
  private readonly injector = inject(Injector);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly propertyStore = inject(PropertyStore);
  private readonly tenantStore = inject(TenantStore);
  private readonly contractStore = inject(ContractStore);
  private readonly inventoryStore = inject(InventoryReportStore);
  private readonly scoring = inject(PropertyScoringService);
  private readonly billingProfiles = inject(WORKSPACE_BILLING_PROFILES_STORE);
  private readonly documentService = inject(DocumentService);
  private readonly providersService = inject(ServiceProvidersService);

  /** Faux tant qu'aucun écran n'a demandé la complétude. Voir l'en-tête. */
  private readonly active = signal(false);

  /**
   * L'identifiant d'espace, mais seulement une fois le service activé — c'est ce
   * qui garde la lecture des documents froide. `documentsExist$()` est un
   * one-shot qui a besoin d'un espace résolu, d'où l'attente des deux.
   */
  private readonly documentsGate = computed(() =>
    this.active() ? this.workspaceStore.currentWorkspaceId() : null
  );

  private readonly hasDocuments = toSignal(
    toObservable(this.documentsGate).pipe(
      filter((id): id is string => !!id),
      take(1),
      switchMap(() => this.documentService.documentsExist$()),
      catchError(() => of(false))
    ),
    { initialValue: false }
  );

  private readonly providerCount = toSignal(
    toObservable(this.active).pipe(
      filter(Boolean),
      take(1),
      switchMap(() =>
        this.providersService.getAllServiceProviders().pipe(
          map(list => list.length),
          catchError(() => of(0))
        )
      )
    ),
    { initialValue: 0 }
  );

  /**
   * Instantané plat des biens, réduit à ce dont la complétude a besoin.
   *
   * Les trois décomptes sont indexés une fois pour toute la liste plutôt que
   * refiltrés par bien : sur un espace de 40 biens et 40 baux, la version naïve
   * fait 1600 comparaisons à chaque recalcul du signal.
   */
  private readonly propertySnapshots = computed<PropertySetupSnapshot[]>(() => {
    const tenantsByProperty = countTenantsByProperty(this.tenantStore.list());
    const contractsByProperty = countByPropertyUID(this.contractStore.list());
    const inventoriesByProperty = countByPropertyUID(this.inventoryStore.list());

    return this.propertyStore.list().map(property => ({
      propertyUID: property.propertyUID,
      name: property.name?.trim() || property.propertyUID,
      listingPercentage: this.scoring.calculatePropertyScore(property).overallPercentage,
      isResidential: !!property.type && isResidential(property.type),
      hasActivePeb: this.hasActivePeb(property),
      tenantCount: tenantsByProperty.get(property.propertyUID) ?? 0,
      contractCount: contractsByProperty.get(property.propertyUID) ?? 0,
      inventoryCount: inventoriesByProperty.get(property.propertyUID) ?? 0
    }));
  });

  /** Le calcul complet — pure fonction, tout le reste n'est que lecture. */
  readonly setup = computed<WorkspaceSetup>(() => {
    const workspace = this.workspaceStore.currentWorkspace();
    const user = this.authStore.user();

    return computeWorkspaceSetup({
      user: user
        ? {
            firstName: user.firstName,
            lastName: user.lastName,
            phoneNumber: user.phoneNumber,
            photoURL: user.photoURL
          }
        : null,
      emailVerified: this.authStore.emailVerified(),
      workspace: workspace
        ? {
            name: workspace.name,
            vatNumber: workspace.billing?.vatNumber,
            billingStreet: workspace.billing?.address?.street,
            billingCity: workspace.billing?.address?.city
          }
        : null,
      isProfessionalWorkspace: !!workspace?.type && PROFESSIONAL_WORKSPACE_TYPES.has(workspace.type),
      properties: this.propertySnapshots(),
      providerCount: this.providerCount(),
      hasDocuments: this.hasDocuments(),
      hasBankAccount: this.hasBankAccount()
    });
  });

  /**
   * Vrai tant que la complétude ne peut pas être calculée honnêtement
   * (DESIGN.md §13.3 — mieux vaut ne rien annoncer qu'un faux résultat).
   *
   * Ce que cette garde couvre RÉELLEMENT : la fenêtre où aucun espace n'est
   * encore résolu. `propertyStore.loading()` est ajouté par correction, mais
   * PERSONNE ne l'écrit aujourd'hui dans l'app (`setLoading` n'apparaît que dans
   * la spec du store) : il est donc constamment faux. Ne pas s'y fier.
   */
  readonly loading = computed(
    () => !this.workspaceStore.hasActiveWorkspace() || this.propertyStore.loading()
  );

  /**
   * Reste-t-il quelque chose à faire ? Pilote l'état « tout est complet ».
   *
   * Lu sur `doneCount`/`total` et non sur `items` : depuis #240 cette liste ne
   * contient que les biens DÉPLIÉS. Un espace de dix biens en retard n'en
   * affiche que trois, et « tous les items affichés sont faits » aurait pu
   * devenir vrai alors que sept biens attendaient encore.
   */
  readonly allDone = computed(() => {
    const setup = this.setup();
    return setup.total > 0 && setup.doneCount === setup.total;
  });

  readonly remainingCount = computed(() => {
    const setup = this.setup();
    return setup.total - setup.doneCount;
  });

  /** Le seul chiffre que l'aside affiche — voir `WORKSPACE_SETUP_PROGRESS`. */
  readonly percentage = computed(() => this.setup().percentage);

  /**
   * Branche les lectures dont la complétude dépend. Idempotent : plusieurs
   * écrans peuvent l'appeler, le travail n'est fait qu'une fois.
   *
   * Instancier ces trois services suffit à brancher leur écoute Firestore, qui
   * alimente `TenantStore` / `ContractStore` / `InventoryReportStore`.
   */
  activate(): void {
    if (this.active()) return;
    this.active.set(true);

    this.injector.get(TenantsService);
    this.injector.get(ContractsService);
    this.injector.get(InventoryReportService);

    this.billingProfiles.loadBillingProfiles();
  }

  private hasActivePeb(property: Property): boolean {
    return (property.technical?.pebCertificates ?? []).some(
      certificate => certificate.status === CertificateStatus.Active
    );
  }

  /**
   * Un IBAN exploitable existe-t-il ? On lit `bankingInfo.iban` défensivement :
   * les profils viennent de Firestore en objets nus, pas forcément en instances
   * de `BillingProfile` — appeler `isValid()` dessus casserait.
   */
  private hasBankAccount(): boolean {
    return this.billingProfiles
      .profiles()
      .some(profile => (profile?.bankingInfo?.iban ?? '').trim().length > 0);
  }
}
